import { useMemo, useState } from 'react';
import { Button, DatePicker, Input, Select, Switch, Table, Tag, Tooltip } from 'antd';
import type { TableColumnsType, TimeRangePickerProps } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { ReloadOutlined } from '@ant-design/icons';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import VoipCallTimeline from '../../../components/leedflow_voip_page/VoipCallTimeline';
import { MISSED_REASON_COLORS, MISSED_REASON_LABELS } from '../../../components/leedflow_voip_page/missedReasonLabels';
import { useVoipCalls } from '../../../hooks/leedflow/useVoipCalls';
import { MissedReason, VoipCallRow } from '../../../../domain/use_cases/leedflow/voipCalls/listVoipCalls';

const RANGE_PRESETS: TimeRangePickerProps['presets'] = [
  { label: "Aujourd'hui", value: [dayjs().startOf('day'), dayjs()] },
  { label: '7 derniers jours', value: [dayjs().add(-7, 'd'), dayjs()] },
  { label: '14 derniers jours', value: [dayjs().add(-14, 'd'), dayjs()] },
  { label: '30 derniers jours', value: [dayjs().add(-30, 'd'), dayjs()] },
];

const STATUS_COLORS: Record<string, string> = {
  started: 'gold',
  active: 'blue',
  finished: 'green',
};

const VoipCallsTab: React.FC = () => {
  const [period, setPeriod] = useState<[Dayjs | null, Dayjs | null]>([dayjs().add(-7, 'd'), dayjs()]);
  const [status, setStatus] = useState<string | undefined>(undefined);
  const [direction, setDirection] = useState<string | undefined>(undefined);
  const [missedReason, setMissedReason] = useState<MissedReason | undefined>(undefined);
  const [onlyFailed, setOnlyFailed] = useState<boolean>(false);
  const [onlyAnomalies, setOnlyAnomalies] = useState<boolean>(false);
  const [search, setSearch] = useState<string>('');

  // A single search box: the backend matches it against the remote party OR the
  // Leedflow DID, so either side of the call resolves.
  const filters = useMemo(
    () => ({
      status,
      direction,
      missedReason,
      onlyFailed: onlyFailed || undefined,
      onlyAnomalies: onlyAnomalies || undefined,
      search: search || undefined,
    }),
    [status, direction, missedReason, onlyFailed, onlyAnomalies, search],
  );

  const { calls, status: loadStatus, reload } = useVoipCalls(period, filters);

  const columns: TableColumnsType<VoipCallRow> = [
    {
      title: 'Date',
      dataIndex: 'created',
      key: 'created',
      width: 160,
      render: (created: number | null) => (created ? dayjs(created).format('DD/MM/YYYY HH:mm:ss') : '—'),
    },
    {
      title: 'Sens',
      dataIndex: 'direction',
      key: 'direction',
      width: 100,
      render: (d: string | null) =>
        d ? <Tag color={d === 'inbound' ? 'geekblue' : 'purple'}>{d === 'inbound' ? 'Entrant' : 'Sortant'}</Tag> : '—',
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (s: string | null) => (s ? <Tag color={STATUS_COLORS[s] ?? 'default'}>{s}</Tag> : '—'),
    },
    {
      title: 'Correspondant',
      dataIndex: 'party',
      key: 'party',
      render: (party: string | null, row) => (
        <span>
          {party ?? '—'}
          {row.contactName ? <span style={{ color: '#888' }}> ({row.contactName})</span> : null}
        </span>
      ),
    },
    {
      title: 'DID Leedflow',
      dataIndex: 'did',
      key: 'did',
      render: (did: string | null) => did ?? '—',
    },
    {
      title: 'Owner',
      dataIndex: 'ownerEmail',
      key: 'ownerEmail',
      render: (email: string | null) => email ?? '—',
    },
    {
      title: 'Durée',
      dataIndex: 'durationSeconds',
      key: 'durationSeconds',
      width: 90,
      render: (s: number | null) => (typeof s === 'number' ? `${s}s` : '—'),
    },
    {
      title: 'Cause manqué',
      dataIndex: 'missedReason',
      key: 'missedReason',
      width: 180,
      render: (reason: MissedReason | null, row) =>
        row.missed && reason ? (
          <Tag color={MISSED_REASON_COLORS[reason]}>{MISSED_REASON_LABELS[reason]}</Tag>
        ) : row.missed ? (
          '—'
        ) : null,
    },
    {
      title: 'Échecs',
      dataIndex: 'failuresCount',
      key: 'failuresCount',
      width: 90,
      render: (count: number) =>
        count > 0 ? (
          <Tooltip title="Erreurs stampées — déplier pour le détail">
            <Tag color="red">{count}</Tag>
          </Tooltip>
        ) : (
          <Tag color="green">0</Tag>
        ),
    },
    {
      title: 'Scénario',
      dataIndex: 'scenarioLabel',
      key: 'scenarioLabel',
      width: 200,
      render: (label: string) => <Tag>{label}</Tag>,
    },
    {
      // Distinct de « Échecs » : un évènement manquant ou dans le mauvais ordre
      // n'est jamais une erreur stampée, donc il compte 0 échec.
      title: 'Écarts',
      dataIndex: 'anomaliesCount',
      key: 'anomaliesCount',
      width: 90,
      render: (count: number) =>
        count > 0 ? (
          <Tooltip title="Écarts à la séquence attendue du scénario — déplier pour le détail">
            <Tag color="orange">{count}</Tag>
          </Tooltip>
        ) : (
          <Tag color="green">0</Tag>
        ),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', margin: '0 0 1rem 0', alignItems: 'center' }}>
        <Input.Search
          allowClear
          placeholder="Numéro (correspondant ou DID)"
          style={{ maxWidth: 280 }}
          loading={loadStatus === ContextStatus.LOADING}
          onSearch={(value) => setSearch(value ?? '')}
          onChange={(e) => {
            if (!e.target.value) setSearch('');
          }}
        />
        <DatePicker.RangePicker
          placeholder={['Début', 'Fin']}
          format="DD/MM/YYYY"
          presets={RANGE_PRESETS}
          value={period}
          disabledDate={(current) => (current ? current.isAfter(dayjs(), 'day') : false)}
          onChange={(dates) => setPeriod(dates ? [dates[0], dates[1]] : [null, null])}
        />
        <Select
          allowClear
          placeholder="Statut"
          style={{ minWidth: 140 }}
          value={status}
          onChange={setStatus}
          options={[
            { value: 'started', label: 'started' },
            { value: 'active', label: 'active' },
            { value: 'finished', label: 'finished' },
          ]}
        />
        <Select
          allowClear
          placeholder="Sens"
          style={{ minWidth: 140 }}
          value={direction}
          onChange={setDirection}
          options={[
            { value: 'inbound', label: 'Entrant' },
            { value: 'outbound', label: 'Sortant' },
          ]}
        />
        <Select
          allowClear
          placeholder="Cause manqué"
          style={{ minWidth: 200 }}
          value={missedReason}
          onChange={setMissedReason}
          options={Object.entries(MISSED_REASON_LABELS).map(([value, label]) => ({ value, label }))}
        />
        <span>
          <Switch checked={onlyFailed} onChange={setOnlyFailed} />{' '}
          <span style={{ marginLeft: 6 }}>Échecs uniquement</span>
        </span>
        <span>
          <Switch checked={onlyAnomalies} onChange={setOnlyAnomalies} />{' '}
          <span style={{ marginLeft: 6 }}>Écarts au modèle</span>
        </span>
        <Button type="primary" icon={<ReloadOutlined />} onClick={() => reload()}>
          Rafraîchir
        </Button>
      </div>

      <Table<VoipCallRow>
        rowKey="roomName"
        size="small"
        columns={columns}
        dataSource={calls}
        loading={loadStatus === ContextStatus.LOADING}
        pagination={{ pageSize: 25, showSizeChanger: true }}
        expandable={{
          expandedRowRender: (row) => <VoipCallTimeline roomName={row.roomName} docId={row.id} />,
        }}
      />
    </>
  );
};

export default VoipCallsTab;
